import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.js';
import { sendEmailVerification } from 'firebase/auth';

const VerifyEmail = () => {
  const { currentUser, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(60);
  const [canResend, setCanResend] = useState(false);
  
  const navigate = useNavigate();
  
  // Redirect if no user is logged in
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    }
  }, [currentUser, navigate]);
  
  // Countdown for resend button
  useEffect(() => {
    if (!canResend && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
      
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setCanResend(true);
    }
  }, [timeLeft, canResend]);
  
  const handleResendVerification = async () => {
    try {
      setLoading(true);
      setError('');
      setMessage('');
      
      if (!currentUser) {
        throw new Error('No user is currently logged in');
      }
      
      await sendEmailVerification(currentUser);
      setMessage('Verification email sent! Please check your inbox.');
      setCanResend(false);
      setTimeLeft(60);
    } catch (err) {
      console.error('Error sending verification email:', err);
      setError(err.message || 'Failed to send verification email');
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Error logging out:', err);
      setError(err.message || 'Failed to log out');
    }
  };
  
  // If there's no current user, we show nothing (redirect happens in useEffect)
  if (!currentUser) {
    return null;
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-md">
        <div>
          <h2 className="text-center text-3xl font-extrabold text-gray-900">
            Verify your email
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            We've sent a verification email to <span className="font-medium">{currentUser.email}</span>
          </p>
        </div>
        
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4">
            <div className="flex">
              <div>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}
        
        {message && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4">
            <div className="flex">
              <div>
                <p className="text-sm text-green-700">{message}</p>
              </div>
            </div>
          </div>
        )}
        
        <div className="bg-blue-50 p-4 rounded-md">
          <h3 className="text-md font-medium text-blue-800">What to do next:</h3>
          <ul className="mt-2 ml-4 list-disc text-sm text-blue-700 space-y-1">
            <li>Check your email inbox for a verification link</li>
            <li>Check your spam folder if you don't see it</li>
            <li>Click the link in the email to verify your account</li>
            <li>After verification, return to the app and log in again</li>
          </ul>
        </div>
        
        <div className="flex flex-col space-y-4">
          <button
            onClick={handleResendVerification}
            disabled={loading || !canResend}
            className={`py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              (loading || !canResend) ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {loading ? 'Sending...' : 
             canResend ? 'Resend verification email' : `Resend available in ${timeLeft}s`}
          </button>
          
          <div className="flex justify-center space-x-4">
            <button
              onClick={handleLogout}
              className="text-sm font-medium text-blue-600 hover:text-blue-500"
            >
              Log out
            </button>
            <Link
              to="/"
              className="text-sm font-medium text-blue-600 hover:text-blue-500"
            >
              Return to home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail; 